import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { geocodeAddress } from "@/lib/geocoding";
import L from "leaflet";
import "@/lib/leaflet-setup";

// Kurikka, Etelä-Pohjanmaa
const KURIKKA_CENTER: L.LatLngExpression = [62.2584, 22.4094];

interface CemeteryMarker {
  id: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  lat: number;
  lng: number;
}

export default function CemeteriesMap() {
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [markers, setMarkers] = useState<CemeteryMarker[]>([]);

  // Fetch cemeteries
  const { data: cemeteries, isLoading } = useQuery({
    queryKey: ["cemeteries-for-map"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cemeteries")
        .select("*")
        .is("deleted_at", null)
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  // Geocode cemeteries without coordinates
  useEffect(() => {
    if (!cemeteries) return;

    const geocodeCemeteries = async () => {
      setIsGeocoding(true);
      const newMarkers: CemeteryMarker[] = [];

      for (const cemetery of cemeteries) {
        let lat = cemetery.latitude ? Number(cemetery.latitude) : null;
        let lng = cemetery.longitude ? Number(cemetery.longitude) : null;

        if (lat === null || lng === null) {
          const coords = await geocodeAddress(cemetery.address, cemetery.postal_code, cemetery.city);
          if (coords) {
            lat = coords.lat;
            lng = coords.lng;
            // Save coordinates to database
            await supabase
              .from("cemeteries")
              .update({ latitude: lat, longitude: lng })
              .eq("id", cemetery.id);
          }
        }

        if (lat !== null && lng !== null) {
          newMarkers.push({
            id: cemetery.id,
            name: cemetery.name,
            address: cemetery.address,
            postalCode: cemetery.postal_code,
            city: cemetery.city,
            lat,
            lng,
          });
        }
      }

      setMarkers(newMarkers);
      setIsGeocoding(false);
    };

    geocodeCemeteries();
  }, [cemeteries]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current).setView(KURIKKA_CENTER, 10);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapRef.current);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Add markers to map
  useEffect(() => {
    if (!mapRef.current || markers.length === 0) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Add new markers
    markers.forEach((cemetery) => {
      const marker = L.marker([cemetery.lat, cemetery.lng])
        .addTo(mapRef.current!)
        .bindPopup(`
          <div style="min-width: 150px;">
            <strong>${cemetery.name}</strong><br/>
            <span style="font-size: 12px;">${cemetery.address}</span><br/>
            <span style="font-size: 12px;">${cemetery.postalCode} ${cemetery.city}</span>
          </div>
        `);
      markersRef.current.push(marker);
    });

    // Fit map to markers
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers]);

  const showLoading = isLoading || isGeocoding;

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/cemeteries")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Takaisin hautausmaihin
            </Button>
            <h1 className="text-2xl font-bold">Hautausmaat kartalla</h1>
          </div>
          <span className="text-sm text-muted-foreground">
            {markers.length} hautausmaata kartalla
          </span>
        </div>

        <div className="relative h-[calc(100vh-200px)] rounded-lg overflow-hidden border">
          {showLoading && (
            <div className="absolute inset-0 bg-background/80 flex flex-col items-center justify-center z-[1000] gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-muted-foreground">
                {isLoading ? "Ladataan hautausmaita..." : "Haetaan sijainteja osoitteista..."}
              </span>
            </div>
          )}
          <div ref={mapContainerRef} className="h-full w-full isolate" />
        </div>
      </div>
    </Layout>
  );
}
